"use client";

import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Cpu, Search, Wrench, CircleCheck as CheckCircle2, TrendingUp } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
const DEFAULT_MASCOT = {
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/8cc77ff8-9509-4afd-995d-58f58605cc2a.png",
  alt: "Roboto Mascot"
};
const copy = {
  eyebrow: {
    vi: "Hồ sơ dự án Lab",
    en: "Lab Portfolio"
  },
  heading: {
    vi: "Dự án thực tế học viên chế tạo",
    en: "What our students build"
  },
  subheading: {
    vi: "Mọi dự án đều do chính học viên lên ý tưởng, đấu nối mạch và lập trình Coding. Ứng dụng vi điều khiển chuẩn công nghiệp cùng cảm biến độ chính xác cao, các em biến lý thuyết Engineering và Robotics thành cỗ máy vận hành thực thụ.",
    en: "Every project is conceived, wired, and coded by our students. Working with industry-standard microcontrollers and precision sensors, they turn engineering theory into working machinery."
  },
  viewAllBtn: {
    vi: "Xem tất cả dự án",
    en: "View all projects"
  },
  inspectBuild: {
    vi: "Xem chi tiết dự án",
    en: "Inspect build"
  },
  featuredBadge: {
    vi: "Dự án tiêu biểu",
    en: "Featured Build"
  },
  ctaTitle: {
    vi: "Sẵn sàng chứng kiến tiềm năng Engineering vượt trội của con?",
    en: "Ready to see what your child can engineer?"
  },
  ctaDesc: {
    vi: "Đăng ký ngay buổi trải nghiệm 60 phút có giáo viên hướng dẫn tại phòng Lab Robotics chuyên nghiệp.",
    en: "Join a guided 60-minute trial session in our robotics laboratory."
  },
  ctaBtn: {
    vi: "Đăng ký học thử miễn phí",
    en: "Book a free trial"
  },
  noProjects: {
    vi: "Chưa có dự án nào được công bố.",
    en: "No project records found."
  }
};
const processSteps = [{
  id: "explore",
  stepNum: "01",
  titleVi: "KHÁM PHÁ",
  titleEn: "EXPLORE",
  descVi: "Tìm hiểu nguyên lý & ý tưởng chế tạo",
  descEn: "Inquire, investigate & form ideas"
}, {
  id: "build",
  stepNum: "02",
  titleVi: "LẮP RÁP",
  titleEn: "BUILD",
  descVi: "Lắp ráp cơ khí, đấu mạch & lập trình",
  descEn: "Construct chassis, circuits & code"
}, {
  id: "test",
  stepNum: "03",
  titleVi: "THỬ NGHIỆM",
  titleEn: "TEST",
  descVi: "Vận hành cảm biến & gỡ lỗi hoạt động",
  descEn: "Run sensors, calibrate & debug"
}, {
  id: "improve",
  stepNum: "04",
  titleVi: "CẢI TIẾN",
  titleEn: "IMPROVE",
  descVi: "Tối ưu hóa thuật toán & hoàn thiện",
  descEn: "Iterate, optimize & finalize build"
}];
const studentRoles = {
  vi: "NHÀ CHẾ TẠO + LẬP TRÌNH VIÊN + NHÀ THIẾT KẾ + NGƯỜI GIẢI QUYẾT VẤN ĐỀ",
  en: "BUILDERS + CODERS + DESIGNERS + PROBLEM SOLVERS"
};
interface ProjectGridConsumerProps {
  lang: "vi" | "en";
  shouldReduceMotion: boolean | null;
}
function ProjectGridConsumer({
  lang,
  shouldReduceMotion
}: ProjectGridConsumerProps) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-6 lg:gap-8">
        {[0, 1, 2].map(i => <div key={i} className={cn("rounded-xl border border-border bg-card p-6 animate-pulse min-h-[340px] w-full", i === 0 ? "col-span-1 md:col-span-2 lg:col-span-2 lg:row-span-2" : "col-span-1 md:col-span-1 lg:col-span-1 lg:row-span-1")}>
            <div className={cn("w-full bg-muted rounded-lg mb-4", i === 0 ? "aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/11]" : "aspect-[16/9] lg:aspect-[16/8]")} />
            <div className="h-4 w-1/4 bg-muted rounded mb-3" />
            <div className="h-6 w-3/4 bg-muted rounded mb-2" />
            <div className="h-4 w-full bg-muted rounded mb-2" />
            <div className="h-4 w-2/3 bg-muted rounded" />
          </div>)}
      </div>;
  }
  if (error || !posts || posts.length === 0) {
    return <div className="rounded-xl border border-dashed border-border bg-card p-8 text-center">
        <p className="font-mono text-sm text-muted-foreground">{copy.noProjects[lang]}</p>
      </div>;
  }
  const displayPosts = posts.slice(0, 3);
  return <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
    once: true,
    amount: 0.15
  }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:grid-rows-2 gap-6 lg:gap-8">
      {displayPosts.map((post: any, i: number) => {
      const acf = post.acf || {};
      const resolveImageUrl = (img: any): string => {
        if (!img) return "";
        if (typeof img === "string") return img;
        if (typeof img === "object" && typeof img.url === "string") return img.url;
        return "";
      };
      const imageSrc = resolveImageUrl(post.customFields?.image) || resolveImageUrl(post.acf?.image) || (typeof post.featuredImage === "string" ? post.featuredImage : "") || "";
      const image = {
        src: imageSrc,
        srcset: "",
        sizes: ""
      };
      return <motion.div key={post.id} data-index={i} variants={itemVariants} whileHover={shouldReduceMotion ? {} : {
        y: -6
      }} transition={{
        duration: 0.25,
        ease: "easeOut"
      }} className={cn("h-full w-full", i === 0 ? "col-span-1 md:col-span-2 lg:col-span-2 lg:row-span-2" : "col-span-1 md:col-span-1 lg:col-span-1 lg:row-span-1")}>
            <Card className="group h-full overflow-hidden border border-border bg-card shadow-sm hover:border-secondary hover:shadow-xl hover:ring-1 hover:ring-secondary/40 transition-[border-color,box-shadow,ring] duration-250 flex flex-col justify-between rounded-xl">
              <div>
                {/* Project Image with Distinct Aspect Ratio & Overlapping Treatment */}
                <div className={cn("relative w-full overflow-hidden bg-muted", i === 0 ? "aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/11]" : "aspect-[16/9] lg:aspect-[16/8]")}>
                  {image.src ? <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={acf.project_name || "Project build"} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-108" /> : <div className="h-full w-full flex items-center justify-center bg-muted text-muted-foreground">
                      <Cpu className="size-12 opacity-30 text-secondary" />
                    </div>}

                  {/* Darkened gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent opacity-35 group-hover:opacity-75 transition-opacity duration-500 pointer-events-none" />

                  {/* Age Range Badge */}
                  {acf.age_range && <div className="absolute top-3.5 left-3.5 sm:left-4 z-10">
                      <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-lg bg-background/90 text-foreground border border-border/80 shadow-xs backdrop-blur-xs">
                        {acf.age_range}
                      </span>
                    </div>}

                  {/* Subtle Overlapping Label/Tag on Featured Card */}
                  {i === 0 && <div className="absolute -bottom-3.5 left-5 sm:left-6 z-20 flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-md bg-secondary text-secondary-foreground shadow-sm border border-secondary/50">
                        <span className="size-1.5 rounded-xs bg-secondary-foreground/75" />
                        {copy.featuredBadge[lang]}
                      </span>
                      {acf.field_domain && <span className="hidden sm:inline-flex font-mono text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md bg-background/95 text-foreground border border-border/80 shadow-xs backdrop-blur-xs">
                          {acf.field_domain}
                        </span>}
                    </div>}
                </div>

                {/* Card Content with Refined Spacing */}
                <CardContent className={cn("p-6", i === 0 ? "pt-7 sm:pt-8 lg:p-8" : "p-6")}>
                  {acf.field_domain && <div className={cn("flex items-center gap-2 mb-3", i === 0 && "sm:hidden")}>
                      <div className="p-1.5 rounded-md bg-secondary/10 border border-secondary/20 flex items-center justify-center">
                        <Cpu className="size-4 text-secondary" />
                      </div>
                      <span className="font-mono text-xs text-secondary font-semibold uppercase tracking-wide">
                        {acf.field_domain}
                      </span>
                    </div>}

                  <h3 className={cn("font-serif font-bold text-card-foreground leading-snug mb-3 group-hover:text-primary transition-colors duration-200", i === 0 ? "text-xl sm:text-2xl lg:text-3xl" : "text-lg sm:text-xl")}>
                    {acf.project_name}
                  </h3>

                  {acf.short_description && <p className={cn("text-muted-foreground leading-relaxed", i === 0 ? "text-sm sm:text-base line-clamp-3 lg:line-clamp-4" : "text-sm line-clamp-2")}>
                      {acf.short_description}
                    </p>}
                </CardContent>
              </div>

              {/* Card Footer with Micro-animated Inspect Link */}
              <div className={cn("px-6 pb-6 pt-3 mt-auto border-t border-border/60 flex items-center justify-between text-xs font-mono text-muted-foreground", i === 0 && "lg:px-8")}>
                <span className="uppercase tracking-wider">
                  {acf.field_domain || "Robotics Lab"}
                </span>
                <Link to={post.link || "#"} className="group/link inline-flex items-center gap-1 font-semibold text-primary hover:text-secondary transition-colors">
                  <span>{copy.inspectBuild[lang]}</span>
                  <ArrowUpRight className="size-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover/link:translate-x-1 group-hover/link:-translate-y-1" />
                </Link>
              </div>
            </Card>
          </motion.div>;
    })}
    </motion.div>;
}
export default function StudentBuilds() {
  const shouldReduceMotion = useReducedMotion();
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // Ignore storage access restrictions
    }
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: "vi" | "en";
      }>;
      if (customEvent.detail?.lang && (customEvent.detail.lang === "vi" || customEvent.detail.lang === "en")) {
        setLang(customEvent.detail.lang);
      }
    };
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, []);
  const renderStepIcon = (id: string) => {
    switch (id) {
      case "explore":
        return <Search className="size-4.5 text-secondary" aria-hidden="true" />;
      case "build":
        return <Wrench className="size-4.5 text-secondary" aria-hidden="true" />;
      case "test":
        return <CheckCircle2 className="size-4.5 text-secondary" aria-hidden="true" />;
      case "improve":
        return <TrendingUp className="size-4.5 text-secondary" aria-hidden="true" />;
      default:
        return <Cpu className="size-4.5 text-secondary" aria-hidden="true" />;
    }
  };
  return <section id="what-students-build" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-b border-border">
      {/* Decorative Floating Mascot Visual */}
      <div className="absolute top-3 right-4 sm:top-5 sm:right-6 md:top-6 md:right-8 lg:top-8 lg:right-12 xl:right-16 2xl:right-24 z-10 pointer-events-none select-none" aria-hidden="true">
        <img src={DEFAULT_MASCOT.src} alt="" loading="lazy" decoding="async" className="h-16 w-16 sm:h-20 sm:w-20 md:h-24 md:w-24 lg:h-28 lg:w-28 object-contain bg-transparent pointer-events-none select-none drop-shadow-md" />
      </div>

      <div className="mx-auto w-full max-w-[1440px] px-5 sm:px-8 md:px-12 lg:px-16 xl:px-[240px]">
        {/* Lab Blueprint spec rail accent */}
        <div className="flex items-center gap-2 mb-6">
          <div className="h-1.5 w-1.5 bg-secondary" />
          <div className="h-px w-16 bg-secondary" />
          <span className="font-mono text-xs uppercase tracking-wider text-secondary font-semibold">
            {copy.eyebrow[lang]}
          </span>
        </div>

        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 lg:mb-12">
          <div className="max-w-2xl pr-16 sm:pr-24 md:pr-0">
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
              {copy.heading[lang]}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
              {copy.subheading[lang]}
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold rounded-lg">
              <Link to="/ProjectsCompetitionsPage">
                <span>{copy.viewAllBtn[lang]}</span>
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Engineering Process & Student Positioning Sub-block - 4-Step Infographic */}
        <div className="relative mb-10 lg:mb-14 rounded-xl border border-border bg-card/85 p-4 sm:p-6 lg:p-7 shadow-xs overflow-hidden">
          {/* Calibrated Dotted Connector Rail & Pipeline Tag */}
          <div className="hidden sm:flex items-center gap-2 mb-4 pb-3 border-b border-dotted border-border/80">
            <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground tracking-widest uppercase">
              <span className="inline-block size-1.5 rounded-sm bg-secondary/80" />
              <span>SYS::PROCESS_INFOGRAPHIC</span>
              <span className="text-secondary/60">───····</span>
            </div>
            <div className="h-px flex-1 border-b border-dotted border-border/80" />
            <div className="flex items-center gap-1 font-mono text-[10px] text-secondary tracking-widest uppercase font-semibold">
              <span>01</span>
              <span className="text-muted-foreground/60">/</span>
              <span>04</span>
              <span className="inline-block size-1.5 rounded-sm bg-secondary/80 ml-1" />
            </div>
          </div>

          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
            {/* 4-Step Visual Infographic Flow */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5 sm:gap-4 flex-1">
              {processSteps.map((step, idx) => {
              const title = lang === "vi" ? step.titleVi : step.titleEn;
              const desc = lang === "vi" ? step.descVi : step.descEn;
              return <div key={step.id} data-index={idx} className="relative flex flex-col justify-between rounded-lg border border-border/80 bg-background/60 hover:bg-background/90 p-3.5 sm:p-4 transition-[border-color,background-color] duration-200 hover:border-secondary/50 group">
                    <div>
                      {/* Step Header with Icon, Number & Connector hint */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="size-8 rounded-md bg-secondary/10 border border-secondary/25 flex items-center justify-center shrink-0 group-hover:bg-secondary/20 transition-colors">
                            {renderStepIcon(step.id)}
                          </div>
                          <span className="font-mono text-[11px] font-bold text-secondary uppercase tracking-wider">
                            STEP {step.stepNum}
                          </span>
                        </div>

                        {idx < processSteps.length - 1 && <span className="hidden lg:inline-flex text-muted-foreground/40 font-mono text-xs" aria-hidden="true">
                            <ArrowRight className="size-3.5 text-secondary/70" />
                          </span>}
                      </div>

                      {/* Step Title */}
                      <h4 className="font-serif font-bold text-sm sm:text-base tracking-wide text-foreground mb-1 group-hover:text-primary transition-colors">
                        {title}
                      </h4>

                      {/* Short Descriptive Text */}
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        {desc}
                      </p>
                    </div>

                    {/* Step Base Line Accent */}
                    <div className="mt-3 pt-2 border-t border-border/50 flex items-center justify-between font-mono text-[10px] text-muted-foreground/80">
                      <span className="uppercase tracking-widest">PHASE {step.stepNum}</span>
                      <span className="inline-block size-1 rounded-full bg-secondary" />
                    </div>
                  </div>;
            })}
            </div>

            {/* Positioning line beside infographic */}
            <div className="flex items-center gap-2.5 pt-3 xl:pt-0 border-t xl:border-t-0 xl:border-l xl:border-border/80 xl:pl-6 xl:max-w-[270px] shrink-0">
              <div className="h-2 w-2 rounded-xs bg-secondary shrink-0" />
              <p className="font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground leading-relaxed">
                {studentRoles[lang]}
              </p>
            </div>
          </div>
        </div>

        {/* Project Cards Grid - Powered by PostsProvider */}
        <PostsProvider wp_query={{
        post_type: "project",
        per_page: 3,
        status: ["publish"]
      }}>
          <ProjectGridConsumer lang={lang} shouldReduceMotion={shouldReduceMotion} />
        </PostsProvider>

        {/* Bottom CTA banner */}
        <div className="mt-12 lg:mt-16 rounded-xl border border-border bg-muted/40 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h4 className="font-serif text-lg font-bold text-foreground">
              {copy.ctaTitle[lang]}
            </h4>
            <p className="text-sm text-muted-foreground mt-1">
              {copy.ctaDesc[lang]}
            </p>
          </div>
          <Button asChild className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-6 py-3 rounded-lg shadow-sm whitespace-nowrap shrink-0">
            <Link to="/FreeTrialContactPage">{copy.ctaBtn[lang]}</Link>
          </Button>
        </div>
      </div>
    </section>;
}